import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { streamChat } from '../api/ollama';
import Markdown from '../components/Markdown';
import StatGrid from '../components/StatGrid';
import StatusPill from '../components/StatusPill';
import useOllamaServer from '../hooks/useOllamaServer';
import useSessionState, { readSession, useSessionId, writeSession } from '../hooks/useSessionState';
import { OLLAMA_HOST_LABEL } from '../config';
import {
    formatBytes,
    formatCountdown,
    formatDuration,
    formatNumber,
    formatTime,
    formatTokensPerSecond,
} from '../utils/format';

const MESSAGES_KEY = 'lfo.chat.messages';
const MODEL_KEY = 'lfo.chat.model';
const STATS_KEY = 'lfo.chat.stats';

const EMPTY_STATS = { requests: 0, promptTokens: 0, completionTokens: 0, evalMs: 0, totalMs: 0 };

const SUGGESTIONS = [
    'Fasse mir die wichtigsten Punkte aus diesem Text zusammen.',
    'Formuliere eine höfliche Absage für eine Terminanfrage.',
    'Erkläre mir den Unterschied zwischen Median und Mittelwert.',
];

let messageCounter = 0;
const nextId = () => `m${Date.now().toString(36)}-${(messageCounter += 1)}`;

function ChatTool() {
    const server = useOllamaServer();
    const sessionId = useSessionId();

    const [messages, setMessages] = useState(() => readSession(MESSAGES_KEY, []));
    const [model, setModel] = useSessionState(MODEL_KEY, '');
    const [stats, setStats] = useSessionState(STATS_KEY, EMPTY_STATS);

    const [input, setInput] = useState('');
    const [streaming, setStreaming] = useState(false);
    const [pendingModel, setPendingModel] = useState(null);
    const [pendingRequest, setPendingRequest] = useState(null);
    const [error, setError] = useState(null);

    const abortRef = useRef(null);
    const bufferRef = useRef('');
    const thinkingBufferRef = useRef('');
    const frameRef = useRef(0);
    const scrollRef = useRef(null);
    const stickToBottomRef = useRef(true);
    const textareaRef = useRef(null);

    const { busyModel, busyExpiresAt, online, models, loaded, vramBytes, reportSelfActivity } = server;

    // --- Verlauf nur im Tab halten (gedrosselt, damit das Streaming fluessig bleibt)
    useEffect(() => {
        const timer = setTimeout(() => writeSession(MESSAGES_KEY, messages), 400);
        return () => clearTimeout(timer);
    }, [messages]);

    // --- Modellauswahl vorbelegen, sobald die Liste da ist
    useEffect(() => {
        if (!model && models.length) {
            setModel(models[0].name);
        }
    }, [model, models, setModel]);

    // --- Wartender Modellwechsel: ausfuehren, sobald niemand mehr generiert
    useEffect(() => {
        if (pendingModel && !busyModel) {
            setModel(pendingModel);
            setPendingModel(null);
        }
    }, [pendingModel, busyModel, setModel]);

    // --- Auto-Scroll nur, wenn der Nutzer unten steht
    useEffect(() => {
        const node = scrollRef.current;
        if (node && stickToBottomRef.current) {
            node.scrollTop = node.scrollHeight;
        }
    });

    const handleScroll = () => {
        const node = scrollRef.current;
        if (!node) return;
        const distance = node.scrollHeight - node.scrollTop - node.clientHeight;
        stickToBottomRef.current = distance < 80;
    };

    /** Gepufferte Token-Ausgabe: max. ein Re-Render pro Frame. */
    const flushBuffer = useCallback((messageId) => {
        if (frameRef.current) return;
        frameRef.current = requestAnimationFrame(() => {
            frameRef.current = 0;
            const chunk = bufferRef.current;
            const thought = thinkingBufferRef.current;
            if (!chunk && !thought) return;
            bufferRef.current = '';
            thinkingBufferRef.current = '';
            setMessages((current) =>
                current.map((message) =>
                    message.id === messageId
                        ? {
                              ...message,
                              content: message.content + chunk,
                              thinking: (message.thinking || '') + thought,
                          }
                        : message
                )
            );
        });
    }, []);

    const runRequest = useCallback(
        async (history, requestModel) => {
            const assistantId = nextId();
            setStreaming(true);
            setError(null);
            stickToBottomRef.current = true;
            setMessages((current) => [
                ...current,
                { id: assistantId, role: 'assistant', content: '', at: Date.now(), model: requestModel },
            ]);

            const controller = new AbortController();
            abortRef.current = controller;
            bufferRef.current = '';
            thinkingBufferRef.current = '';
            reportSelfActivity(requestModel);

            try {
                const result = await streamChat({
                    model: requestModel,
                    messages: history,
                    signal: controller.signal,
                    onToken: (token) => {
                        bufferRef.current += token;
                        reportSelfActivity(requestModel);
                        flushBuffer(assistantId);
                    },
                    onThinking: (thought) => {
                        thinkingBufferRef.current += thought;
                        reportSelfActivity(requestModel);
                        flushBuffer(assistantId);
                    },
                });

                if (frameRef.current) {
                    cancelAnimationFrame(frameRef.current);
                    frameRef.current = 0;
                }
                const rest = bufferRef.current;
                const restThinking = thinkingBufferRef.current;
                bufferRef.current = '';
                thinkingBufferRef.current = '';

                setMessages((current) =>
                    current.map((message) =>
                        message.id === assistantId
                            ? {
                                  ...message,
                                  content: message.content + rest,
                                  thinking: (message.thinking || '') + restThinking || undefined,
                                  stats: result || undefined,
                              }
                            : message
                    )
                );

                if (result) {
                    setStats((current) => ({
                        requests: current.requests + 1,
                        promptTokens: current.promptTokens + result.promptTokens,
                        completionTokens: current.completionTokens + result.completionTokens,
                        evalMs: current.evalMs + result.evalDurationMs,
                        totalMs: current.totalMs + result.totalDurationMs,
                    }));
                }
            } catch (requestError) {
                const aborted = requestError.name === 'AbortError';
                const rest = bufferRef.current;
                const restThinking = thinkingBufferRef.current;
                bufferRef.current = '';
                thinkingBufferRef.current = '';

                setMessages((current) =>
                    current
                        .map((message) =>
                            message.id === assistantId
                                ? {
                                      ...message,
                                      content: message.content + rest,
                                      thinking:
                                          (message.thinking || '') + restThinking || undefined,
                                      stopped: aborted || undefined,
                                  }
                                : message
                        )
                        // Leere Antwort nach Abbruch/Fehler nicht stehen lassen
                        .filter(
                            (message) =>
                                message.id !== assistantId ||
                                message.content.trim() ||
                                message.thinking
                        )
                );

                if (!aborted) {
                    setError(requestError.message || 'Die Anfrage ist fehlgeschlagen.');
                }
            } finally {
                abortRef.current = null;
                setStreaming(false);
            }
        },
        [flushBuffer, reportSelfActivity, setStats]
    );

    // --- Wartende Anfrage starten, sobald der Server frei ist
    useEffect(() => {
        if (!pendingRequest || streaming) return;
        if (busyModel) return; // jemand anderes generiert gerade -> weiter warten
        if (online !== true) return;

        const request = pendingRequest;
        setPendingRequest(null);
        runRequest(request.history, request.model);
    }, [pendingRequest, streaming, busyModel, online, runRequest]);

    const send = (text) => {
        const content = text.trim();
        if (!content || streaming || pendingRequest) return;
        // Steht ein Modellwechsel in der Warteschlange, gilt bereits das neue Modell.
        const targetModel = pendingModel || model;
        if (!targetModel) {
            setError('Es ist kein Modell verfügbar.');
            return;
        }

        const userMessage = { id: nextId(), role: 'user', content, at: Date.now() };
        const history = [...messages, userMessage].map(({ role, content: c }) => ({ role, content: c }));

        setMessages((current) => [...current, userMessage]);
        setInput('');
        setError(null);
        stickToBottomRef.current = true;
        // Der Effekt oben startet sofort, falls frei - sonst bleibt es in der Warteschlange.
        setPendingRequest({ history, model: targetModel });
    };

    const stop = () => {
        abortRef.current?.abort();
        setPendingRequest(null);
    };

    const resetChat = () => {
        abortRef.current?.abort();
        setPendingRequest(null);
        setMessages([]);
        setStats(EMPTY_STATS);
        setError(null);
        writeSession(MESSAGES_KEY, []);
    };

    const handleModelChange = (event) => {
        const next = event.target.value;
        if (next === model) return;
        // Wechsel nur, wenn gerade niemand anderes generiert - sonst Warteschlange.
        if (busyModel) {
            setPendingModel(next);
        } else {
            setPendingModel(null);
            setModel(next);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            send(input);
        }
    };

    useEffect(() => {
        const node = textareaRef.current;
        if (!node) return;
        node.style.height = 'auto';
        node.style.height = `${Math.min(node.scrollHeight, 200)}px`;
    }, [input]);

    // --- abgeleitete Anzeige-Werte ----------------------------------------
    const countdown = formatCountdown(busyExpiresAt);
    const queued = Boolean(pendingRequest);

    // 401/403 kommt von der Zugriffsregel im nginx -> Client ist nicht im Hochschulnetz.
    const outsideNetwork = online === false && [401, 403].includes(server.errorStatus);

    const connection = useMemo(() => {
        if (online === null) return { tone: 'idle', label: 'Verbinde…' };
        if (online === false) return { tone: 'error', label: outsideNetwork ? 'Nur im VPN' : 'Offline' };
        if (streaming) return { tone: 'busy', label: 'Antwortet…' };
        if (queued) return { tone: 'queued', label: 'Vorgemerkt' };
        if (pendingModel) return { tone: 'queued', label: 'Wechsel vorgemerkt' };
        if (busyModel) return { tone: 'busy', label: 'Belegt' };
        return { tone: 'ok', label: 'Bereit' };
    }, [online, outsideNetwork, streaming, queued, pendingModel, busyModel]);

    const avgTokensPerSecond = formatTokensPerSecond(stats.completionTokens, stats.evalMs);

    const sessionStats = [
        { label: 'Anfragen', value: formatNumber(stats.requests) },
        { label: 'Tokens gesendet', value: formatNumber(stats.promptTokens) },
        { label: 'Tokens erhalten', value: formatNumber(stats.completionTokens) },
        { label: 'Ø Tempo', value: avgTokensPerSecond },
        { label: 'Rechenzeit', value: formatDuration(stats.totalMs) },
    ];

    const serverStats = [
        {
            label: 'Server',
            value: online === false ? 'offline' : online === null ? '…' : `v${server.version}`,
        },
        // models ist bereits auf chatfaehige Modelle gefiltert (ohne Embedding-Modelle).
        { label: 'Chat-Modelle', value: formatNumber(models.length) },
        { label: 'Modelle geladen', value: formatNumber(loaded.length) },
        { label: 'Belegter VRAM', value: vramBytes ? formatBytes(vramBytes) : '–' },
    ];

    const offline = online === false;
    const disabled = offline || !model;

    return (
        <div className="chat">
            <section className={`card chat-shell${offline ? ' chat-shell-offline' : ''}`} aria-label="Chat">
                <header className="chat-head">
                    <div className="chat-head-main">
                        <h2>Ollama Chat</h2>
                        <StatusPill
                            tone={connection.tone}
                            label={connection.label}
                            title={server.error || undefined}
                        />
                    </div>

                    <div className="chat-head-controls">
                        <label className="model-select">
                            <span className="sr-only">Modell</span>
                            <select
                                /* Ein wartender Wechsel bleibt sichtbar ausgewaehlt. */
                                value={pendingModel || model}
                                onChange={handleModelChange}
                                disabled={!models.length || streaming || offline}
                            >
                                {!models.length && <option value="">Keine Modelle gefunden</option>}
                                {models.map((entry) => (
                                    <option key={entry.name} value={entry.name}>
                                        {entry.name}
                                        {entry.parameterSize ? ` · ${entry.parameterSize}` : ''}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <button type="button" className="btn btn-ghost" onClick={resetChat}>
                            Neuer Chat
                        </button>
                    </div>
                </header>

                {offline && (
                    <div className="offline-panel" role="alert">
                        <span className="offline-icon" aria-hidden="true">
                            {outsideNetwork ? '🔒' : '⚠'}
                        </span>
                        <h3>
                            {outsideNetwork
                                ? 'Chat nur im Hochschulnetz verfügbar'
                                : 'Chat derzeit nicht erreichbar'}
                        </h3>
                        <p>
                            {outsideNetwork ? (
                                <>
                                    Der Ollama-Dienst läuft auf <code>{OLLAMA_HOST_LABEL}</code> und ist
                                    bewusst nicht öffentlich zugänglich. Bitte verbinde dich mit dem VPN
                                    der TU Dortmund oder nutze einen Rechner im Hochschulnetz.
                                </>
                            ) : (
                                <>
                                    Der Ollama-Dienst auf <code>{OLLAMA_HOST_LABEL}</code> antwortet nicht.
                                    Falls du von außerhalb zugreifst, verbinde dich zuerst mit dem VPN der
                                    TU Dortmund.
                                </>
                            )}
                        </p>
                        <button type="button" className="btn btn-primary" onClick={server.refresh}>
                            Erneut versuchen
                        </button>
                        {server.error && <p className="offline-detail">Details: {server.error}</p>}
                    </div>
                )}

                {/* Bei fehlender Verbindung ausgegraut und nicht bedienbar. */}
                <div
                    className={`chat-body${offline ? ' chat-body-locked' : ''}`}
                    aria-hidden={offline || undefined}
                >
                    {busyModel && (
                        <p className="notice notice-busy">
                            <strong>{busyModel}</strong> wird gerade von jemand anderem genutzt.
                            {countdown ? ` Voraussichtlich frei in ${countdown}.` : ''}
                        </p>
                    )}

                    {pendingModel && (
                        <p className="notice notice-queue">
                            <strong>{pendingModel}</strong> ist vorgemerkt – sobald das Modell frei ist,
                            wechseln wir automatisch. Deine Nachrichten gehen an dieses Modell.{' '}
                            <button
                                type="button"
                                className="link-btn"
                                onClick={() => setPendingModel(null)}
                            >
                                Vormerkung aufheben
                            </button>
                        </p>
                    )}

                    {queued && (
                        <p className="notice notice-queue">
                            Deine Nachricht ist vorgemerkt und startet automatisch, sobald der Server frei
                            ist.
                        </p>
                    )}

                    {error && <p className="notice notice-error">{error}</p>}

                    <div className="chat-log" ref={scrollRef} onScroll={handleScroll}>
                        {messages.length === 0 ? (
                            <div className="chat-empty">
                                <h3>Womit kann ich helfen?</h3>
                                <p>
                                    Der Chat läuft vollständig auf den LFO-Servern. Der Verlauf bleibt nur
                                    in diesem Browser-Tab und wird beim Schließen gelöscht.
                                </p>
                                <div className="suggestions">
                                    {SUGGESTIONS.map((suggestion) => (
                                        <button
                                            key={suggestion}
                                            type="button"
                                            onClick={() => send(suggestion)}
                                            disabled={disabled}
                                        >
                                            {suggestion}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            messages.map((message) => (
                                <article key={message.id} className={`msg msg-${message.role}`}>
                                    <div className="msg-avatar" aria-hidden="true">
                                        {message.role === 'user' ? 'Du' : 'AI'}
                                    </div>
                                    <div className="msg-body">
                                        <div className="msg-meta">
                                            <span>
                                                {message.role === 'user'
                                                    ? 'Du'
                                                    : message.model || 'Assistent'}
                                            </span>
                                            <span>{formatTime(message.at)}</span>
                                        </div>
                                        {message.thinking && (
                                            <details className="thinking">
                                                <summary>
                                                    Gedankengang
                                                    {!message.content && streaming ? ' · denkt nach…' : ''}
                                                </summary>
                                                <p>{message.thinking}</p>
                                            </details>
                                        )}
                                        {message.role === 'user' ? (
                                            <p className="msg-text">{message.content}</p>
                                        ) : (
                                            <Markdown content={message.content} />
                                        )}
                                        {message.stats && (
                                            <p className="msg-stats">
                                                {formatNumber(message.stats.completionTokens)} Tokens ·{' '}
                                                {formatTokensPerSecond(
                                                    message.stats.completionTokens,
                                                    message.stats.evalDurationMs
                                                )}{' '}
                                                · {formatDuration(message.stats.totalDurationMs)}
                                            </p>
                                        )}
                                        {message.stopped && <p className="msg-stats">Abgebrochen</p>}
                                    </div>
                                </article>
                            ))
                        )}

                        {streaming && (
                            <div className="typing" aria-live="polite">
                                <span />
                                <span />
                                <span />
                            </div>
                        )}
                    </div>

                    <div className="composer">
                        <textarea
                            ref={textareaRef}
                            value={input}
                            onChange={(event) => setInput(event.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={offline ? 'Nicht verbunden…' : 'Nachricht schreiben…'}
                            rows={1}
                            disabled={disabled}
                        />
                        {streaming || queued ? (
                            <button type="button" className="btn btn-stop" onClick={stop}>
                                Stopp
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => send(input)}
                                disabled={disabled || !input.trim()}
                            >
                                Senden
                            </button>
                        )}
                    </div>
                    <p className="composer-hint">
                        Enter sendet · Umschalt+Enter für eine neue Zeile · Sitzung{' '}
                        {sessionId.slice(0, 8)}
                    </p>
                </div>
            </section>

            <section className="card" aria-label="Nutzung dieser Sitzung">
                <h3 className="card-title">Deine Sitzung</h3>
                <StatGrid items={sessionStats} />
                <p className="card-note">
                    Diese Zahlen gelten nur für diesen Tab und werden beim Schließen verworfen.
                </p>
            </section>

            <section className="card" aria-label="Serverauslastung">
                <h3 className="card-title">Ollama-Server</h3>
                <StatGrid items={serverStats} />
                {loaded.length > 0 && (
                    <ul className="loaded-list">
                        {loaded.map((entry) => (
                            <li key={entry.name}>
                                <span className="loaded-name">{entry.name}</span>
                                <span className="loaded-meta">
                                    {formatBytes(entry.sizeVram || entry.size)}
                                    {server.activeModels.includes(entry.name) ? ' · aktiv' : ' · im Speicher'}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
                {online === false && (
                    <p className="card-note card-note-error">
                        Kein Kontakt zum Ollama-Dienst{server.error ? `: ${server.error}` : ''}.
                    </p>
                )}
            </section>
        </div>
    );
}

export default ChatTool;
